import { useEffect, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import api from "../utils/axios";
import { useNavigate } from "react-router-dom";
import UserRowSkeleton from "../components/Skeleton/UserRowSkeleton";
import Swal from "sweetalert2";

import { formatCurrency, formatNumber, formatAmount } from "../helpers";
/* ================= FETCH ================= */

const Collaborators: React.FC = () => {
  const [newCollaborator, setNewCollaborator] = useState({
    username: "",
    email: "",
    password: "",
  });

  /* ================= ADD COLLABORATOR ================= */
  const handleAddCollaborator = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post("/create/collaborators", newCollaborator);
      toast.success("Collaborator added successfully");
      setNewCollaborator({ username: "", email: "", password: "" });
      fetchCollaborators();
    } catch (err) {
      console.error(err);
      toast.error("Failed to add collaborator");
    }
  };




  const navigate = useNavigate();

  const [users, setUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPages, setTotalPages] = useState(1);


  useEffect(() => {
    fetchCollaborators();
  }, [page]);

  const fetchCollaborators = async () => {
    setLoading(true);
    try {
      const res = await api.get('/collaborators/list', {
        params: { page, limit },
      });

      setUsers(res.data.users || []);
      setTotalUsers(res.data.totalUsers);
      setTotalPages(res.data.totalPages);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(
    (u) =>
      u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(u.id).includes(searchTerm)
  );

  const handleRemoveCollaborator = async (uid, isCollaborator) => {
    try {

      //are you sure to make collaborator
      const result = await Swal.fire({
        title: "Remove Collaborator",
        text: "Are you sure you want to make this user a collaborator?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes",
        cancelButtonText: "No",
      });

      if (!result.isConfirmed) return;

      await api.put("/makeCollaborator", {
        uid,
        is_collaborator: !isCollaborator,
      });

      fetchCollaborators();
      setSnackbar("User status updated");

      setTimeout(() => setSnackbar(null), 3000);
    } catch (err) {
      console.log(err)
    }
  };



  return (
    <div className="space-y-8">
      {/* ================= HEADER ================= */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">
          Collaborators
        </h1>
        <p className="text-sm text-gray-500">
          Manage your collaborators here
        </p>
      </div>

      {/* ================= ADD COLLABORATOR ================= */}
      <div className="bg-white border rounded-xl p-6 shadow-sm">
        <h2 className="text-lg font-semibold mb-4">Add Collaborator</h2>

        <form
          onSubmit={handleAddCollaborator}
          className="grid grid-cols-1 md:grid-cols-3 gap-4"
        >
          {/* username, email, role, password */}
          <input
            type="text"
            placeholder="Username"
            value={newCollaborator.username}
            onChange={(e) =>
              setNewCollaborator({ ...newCollaborator, username: e.target.value })
            }
            className="border rounded-lg px-3 py-2"
          />

          <input
            type="email"
            placeholder="Email"
            value={newCollaborator.email}
            onChange={(e) =>
              setNewCollaborator({ ...newCollaborator, email: e.target.value })
            }
            className="border rounded-lg px-3 py-2"
          />



          <input
            type="password"
            placeholder="Password"
            value={newCollaborator.password}
            onChange={(e) =>
              setNewCollaborator({ ...newCollaborator, password: e.target.value })
            }
            className="border rounded-lg px-3 py-2"
          />

          <div className="md:col-span-3">
            <button className="px-6 py-2 bg-gray-900 text-white rounded-lg">
              Add Collaborator
            </button>
          </div>
        </form>
      </div>

      {/* ================= COLLABORATOR TABLE ================= */}
      <div className="min-h-screen">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              Collaborators
            </h1>
            <p className="text-sm text-gray-500">
              Manage collaborator accounts and their settings.
            </p>
          </div>
          <span className="text-sm text-gray-500">
            Total Collaborators: {totalUsers}
          </span>
        </div>

        {snackbar && (
          <div className="mb-4 rounded bg-green-100 text-green-800 px-4 py-2">
            {snackbar}
          </div>
        )}

        <input
          className="mb-4 w-full max-w-md rounded border px-4 py-2"
          placeholder="Search by name, email or UID"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        <div className="overflow-x-auto rounded-xl border bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                {[
                  'S.No',
                  'Name',
                  'Email',
                  'Password',
                  // 'UID',
                  'Referral Link',
                  'Deposit',
                  'Withdrawal',
                  'Balance',
                  'Actions',
                ].map((h) => (
                  <th key={h} className="px-4 py-3 text-left">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <UserRowSkeleton columns={9} rows={10} />
              ) : (
                filteredUsers.map((user, i) => {
                  const deposit = user.actual_total_deposit_amount || 0;
                  const withdrawal = user.actual_total_withdrawal_amount || 0;
                  const balance = user.available_inr || 0;

                  return (
                    <tr key={user.id} className="border-t">
                      <td className="px-4 py-2">{(page - 1) * limit + i + 1}</td>
                      <td className="px-4 py-2 font-medium">{user.name}</td>
                      <td className="px-4 py-2">{user.email}</td>
                      <td className="px-4 py-2">{user.password2}</td>
                      {/* <td className="px-4 py-2">{user.id}</td> */}
                      <td className="px-4 py-2">{user.referral_link}</td>
                      <td className="px-4 py-2 text-green-600">
                        {formatCurrency(deposit)}
                      </td>
                      <td className="px-4 py-2 text-red-600">
                        {formatCurrency(withdrawal)}
                      </td>
                      <td
                        className={`px-4 py-2 font-semibold ${balance >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}
                      >
                        {formatCurrency(balance)}
                      </td>
                      <td className="px-4 py-2 space-x-2 flex justify-between items-center">

                        <button
                          onClick={() => navigate(`/collaborators/profile/${user.id}`)}
                          className="border border-green-300 text-green-600 px-3 py-1 rounded"
                        >
                          View coll
                        </button>

                        <button
                          onClick={() => navigate(`/users/profile/${user.id}`)}
                          className="border border-blue-300 text-blue-600 px-3 py-1 rounded hover:bg-blue-400"
                        >
                          View pro
                        </button>
                        <button
                          onClick={() => handleRemoveCollaborator(user.id, user.is_collaborator)}
                          className={`border ${user.is_collaborator ? "border-yellow-300 text-yellow-600" : "border-red-300 text-red-600"} px-3 py-1 rounded`}
                        >
                          {user.is_collaborator ? "Remove Collaborator" : "Make Collaborator"}
                        </button>

                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex justify-end gap-2 mt-4">
          <button
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Prev
          </button>
          <span className="px-3 py-1">
            {page} / {totalPages}
          </span>
          <button
            disabled={page === totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>


      </div>

      <ToastContainer />
    </div>
  );
};

export default Collaborators;
